import React, { useState, useEffect, useCallback } from 'react';
import { ImageIcon, Search, CheckCircle, ChevronDown, ChevronUp, Maximize2, X, Flag, MessageSquare, RefreshCw, Loader2, ShieldCheck, AlertTriangle, AlertCircle, Info, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRADE_COLORS = {
  'K': { bg: 'bg-amber-50', border: 'border-amber-200', badge: 'bg-amber-100 text-amber-800' },
  '1': { bg: 'bg-rose-50', border: 'border-rose-200', badge: 'bg-rose-100 text-rose-800' },
  '2': { bg: 'bg-orange-50', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-800' },
  '3-4': { bg: 'bg-emerald-50', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-800' },
  '5-6': { bg: 'bg-sky-50', border: 'border-sky-200', badge: 'bg-sky-100 text-sky-800' },
  '7-8': { bg: 'bg-violet-50', border: 'border-violet-200', badge: 'bg-violet-100 text-violet-800' },
  '9-12': { bg: 'bg-slate-50', border: 'border-slate-200', badge: 'bg-slate-100 text-slate-800' },
};

const BigPictureGalleryTab = () => {
  const [gallery, setGallery] = useState([]);
  const [totalImages, setTotalImages] = useState(0);
  const [totalFlagged, setTotalFlagged] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGrades, setExpandedGrades] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [flagNote, setFlagNote] = useState('');
  const [flagging, setFlagging] = useState(false);
  const [regenerating, setRegenerating] = useState(null); // {grade, context} or 'all'
  const [batchProgress, setBatchProgress] = useState(null); // {current, total, currentContext}
  const [auditResult, setAuditResult] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [showAuditPanel, setShowAuditPanel] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [selected, setSelected] = useState(new Set());   // Set of "grade|context" keys

  const selKey = (grade, context) => `${grade}|${context}`;
  const isSelected = (grade, context) => selected.has(selKey(grade, context));
  const toggleSelected = (grade, context) => {
    setSelected(prev => {
      const next = new Set(prev);
      const k = selKey(grade, context);
      if (next.has(k)) next.delete(k); else next.add(k);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const getToken = () => localStorage.getItem('mosaic_session_token');

  useEffect(() => { fetchGallery(); }, []);

  const fetchGallery = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/big-picture-gallery`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setGallery(res.data.gallery);
      setTotalImages(res.data.total_images);
      setTotalFlagged(res.data.total_flagged || 0);
      const expanded = {};
      res.data.gallery.forEach(g => { expanded[g.grade] = true; });
      setExpandedGrades(expanded);
    } catch (err) {
      toast.error('Failed to load Big Picture gallery');
    } finally {
      setLoading(false);
    }
  };

  const toggleGrade = (grade) => {
    setExpandedGrades(prev => ({ ...prev, [grade]: !prev[grade] }));
  };

  const runAudit = async () => {
    setAuditing(true);
    setShowAuditPanel(true);
    setAuditResult(null);
    try {
      const res = await axios.get(`${API}/superadmin/big-picture-gallery/audit`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        timeout: 30000,
      });
      setAuditResult(res.data);
      if (res.data.overall === 'pass') {
        toast.success(`Image audit passed — ${res.data.stats.with_image}/${res.data.stats.total_listening} images verified`);
      } else {
        toast.error(`Image audit found ${res.data.critical_count} critical issue(s)`);
      }
    } catch (err) {
      toast.error('Audit failed — check server logs');
    } finally {
      setAuditing(false);
    }
  };

  const cleanOrphans = async () => {
    setCleaning(true);
    try {
      const res = await axios.post(`${API}/superadmin/big-picture-gallery/clean-orphans`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` },
        timeout: 30000,
      });
      const { total_deleted, bytes_freed_mb } = res.data;
      toast.success(`Cleaned ${total_deleted} orphaned files (${bytes_freed_mb} MB freed)`);
      // Re-run audit to refresh
      await runAudit();
    } catch (err) {
      toast.error('Cleanup failed — check server logs');
    } finally {
      setCleaning(false);
    }
  };

  const handleFlag = async (grade, gradeLabel, context, note) => {
    setFlagging(true);
    try {
      await axios.post(`${API}/superadmin/big-picture-gallery/flag`,
        { grade, context, note },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success('Image flagged for review');
      setGallery(prev => prev.map(g => g.grade === grade ? {
        ...g, contexts: g.contexts.map(c => c.context === context
          ? { ...c, flagged: true, flag_note: note, flag_by: 'You', flag_at: new Date().toISOString() } : c)
      } : g));
      setTotalFlagged(prev => prev + 1);
      if (lightboxImage && lightboxImage.context === context) {
        setLightboxImage(prev => ({ ...prev, flagged: true, flag_note: note }));
      }
      setFlagNote('');
    } catch (err) {
      toast.error('Failed to flag image');
    } finally {
      setFlagging(false);
    }
  };

  const handleUnflag = async (grade, context) => {
    setFlagging(true);
    try {
      await axios.delete(`${API}/superadmin/big-picture-gallery/flag`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        data: { grade, context },
      });
      toast.success('Flag removed');
      setGallery(prev => prev.map(g => g.grade === grade ? {
        ...g, contexts: g.contexts.map(c => c.context === context
          ? { ...c, flagged: false, flag_note: '', flag_by: '', flag_at: '' } : c)
      } : g));
      setTotalFlagged(prev => Math.max(0, prev - 1));
      if (lightboxImage && lightboxImage.context === context) {
        setLightboxImage(prev => ({ ...prev, flagged: false, flag_note: '' }));
      }
    } catch (err) {
      toast.error('Failed to remove flag');
    } finally {
      setFlagging(false);
    }
  };

  const handleRegenerate = useCallback(async (grade, context) => {
    setRegenerating({ grade, context });
    try {
      const res = await axios.post(`${API}/superadmin/big-picture-gallery/regenerate`,
        { grade, context },
        { headers: { Authorization: `Bearer ${getToken()}` }, timeout: 120000 }
      );
      const { image_url, size_kb } = res.data;
      const cacheBuster = `?v=${Date.now()}`;
      toast.success(`Regenerated: ${context} (${size_kb}KB)`);

      // Update gallery state with new image URL + cache buster
      setGallery(prev => prev.map(g => g.grade === grade ? {
        ...g, contexts: g.contexts.map(c => c.context === context
          ? { ...c, image_url: image_url + cacheBuster, flagged: false, flag_note: '', flag_by: '', flag_at: '' } : c)
      } : g));
      setTotalFlagged(prev => Math.max(0, prev - 1));

      // Update lightbox if open
      if (lightboxImage && lightboxImage.context === context) {
        setLightboxImage(prev => ({ ...prev, image_url: image_url + cacheBuster, flagged: false, flag_note: '' }));
      }
      return true;
    } catch (err) {
      const msg = err.response?.data?.detail || 'Regeneration failed';
      toast.error(`Failed: ${context} — ${msg}`);
      return false;
    } finally {
      setRegenerating(null);
    }
  }, [lightboxImage]);

  // Retry a regen once on failure — helps ride out transient OpenAI 429/500.
  // Waits 2s between the first attempt and the retry.
  const regenerateWithRetry = useCallback(async (grade, context) => {
    const first = await handleRegenerate(grade, context);
    if (first) return { ok: true, attempts: 1 };
    await new Promise(r => setTimeout(r, 2000));
    const second = await handleRegenerate(grade, context);
    return { ok: second, attempts: second ? 2 : 2 };
  }, [handleRegenerate]);

  const handleRegenerateAll = useCallback(async () => {
    // Collect all flagged images
    const flagged = [];
    gallery.forEach(g => {
      g.contexts.forEach(c => {
        if (c.flagged) flagged.push({ grade: g.grade, context: c.context });
      });
    });

    if (flagged.length === 0) {
      toast.info('No flagged images to regenerate');
      return;
    }

    setRegenerating('all');
    setBatchProgress({ current: 0, total: flagged.length, currentContext: '' });
    let successes = 0;
    let retries = 0;

    for (let i = 0; i < flagged.length; i++) {
      setBatchProgress({ current: i + 1, total: flagged.length, currentContext: flagged[i].context });
      const res = await regenerateWithRetry(flagged[i].grade, flagged[i].context);
      if (res.ok) successes++;
      if (res.attempts > 1) retries++;
    }

    setBatchProgress(null);
    setRegenerating(null);
    toast.success(`Regenerated ${successes}/${flagged.length} images${retries ? ` (${retries} retried)` : ''}`);
  }, [gallery, regenerateWithRetry]);

  const handleRegenerateSelected = useCallback(async () => {
    const picks = [...selected].map(k => {
      const [grade, context] = k.split('|');
      return { grade, context };
    });
    if (picks.length === 0) {
      toast.info('No images selected. Click a checkbox on any card to select.');
      return;
    }
    if (!window.confirm(`Regenerate ${picks.length} selected Big Picture image(s)? This uses the MOSAIC content-scoring vendor mosaic-image-engine and takes ~15-20 seconds per image. Failed items will be retried once automatically.`)) return;

    setRegenerating('selected');
    setBatchProgress({ current: 0, total: picks.length, currentContext: '' });
    let successes = 0;
    let retries = 0;
    const failed = [];
    for (let i = 0; i < picks.length; i++) {
      setBatchProgress({ current: i + 1, total: picks.length, currentContext: picks[i].context });
      const res = await regenerateWithRetry(picks[i].grade, picks[i].context);
      if (res.ok) successes++; else failed.push(picks[i].context);
      if (res.attempts > 1) retries++;
    }
    setBatchProgress(null);
    setRegenerating(null);
    clearSelection();
    const retryNote = retries ? ` · ${retries} retried` : '';
    if (failed.length === 0) {
      toast.success(`Regenerated all ${successes}/${picks.length} selected images${retryNote}`);
    } else {
      toast.warning(`Regenerated ${successes}/${picks.length}${retryNote}. Failed after retry: ${failed.slice(0, 3).join(', ')}${failed.length > 3 ? '…' : ''}`);
    }
  }, [selected, regenerateWithRetry]);

  const isRegenerating = (grade, context) => {
    if (!regenerating) return false;
    if (regenerating === 'all') return true;
    return regenerating.grade === grade && regenerating.context === context;
  };

  const filteredGallery = gallery.map(gradeGroup => ({
    ...gradeGroup,
    contexts: gradeGroup.contexts.filter(ctx => {
      const matchesSearch = ctx.context.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ctx.sample_question.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFlag = showFlaggedOnly ? ctx.flagged : true;
      return matchesSearch && matchesFlag;
    }),
  })).filter(g => g.contexts.length > 0);

  const filteredTotal = filteredGallery.reduce((sum, g) => sum + g.contexts.length, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="bp-gallery-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002FA7]" />
        <span className="ml-3 text-gray-500">Loading gallery...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bp-gallery-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Big Picture Gallery
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {totalImages} WIDA-aligned illustrations across {gallery.length} grade bands — all verified text-free
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search contexts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]"
              data-testid="bp-gallery-search"
            />
          </div>
          <button
            onClick={() => setShowFlaggedOnly(!showFlaggedOnly)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
              showFlaggedOnly
                ? 'bg-red-50 border-red-300 text-red-700'
                : totalFlagged > 0
                  ? 'bg-red-50 border-red-200 text-red-600 hover:border-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-400'
            }`}
            data-testid="bp-gallery-flagged-filter"
          >
            <Flag size={13} className={totalFlagged > 0 ? 'fill-current' : ''} />
            {totalFlagged} flagged
          </button>
          {/* Regenerate Selected (multi-select) */}
          {selected.size > 0 && (
            <button
              onClick={handleRegenerateSelected}
              disabled={!!regenerating}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border bg-indigo-600 border-indigo-700 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="bp-gallery-regenerate-selected-btn"
            >
              {regenerating === 'selected' ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <RefreshCw size={13} />
              )}
              {regenerating === 'selected'
                ? `Regenerating ${batchProgress?.current}/${batchProgress?.total}...`
                : `Regenerate Selected (${selected.size})`}
            </button>
          )}
          {selected.size > 0 && regenerating !== 'selected' && (
            <button
              onClick={clearSelection}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border bg-white border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              data-testid="bp-gallery-clear-selection-btn"
            >
              <X size={13} /> Clear
            </button>
          )}
          {/* Regenerate All Flagged */}
          {totalFlagged > 0 && (
            <button
              onClick={handleRegenerateAll}
              disabled={!!regenerating}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border bg-blue-600 border-blue-700 text-white hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="bp-gallery-regenerate-all-btn"
            >
              {regenerating === 'all' ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <RefreshCw size={13} />
              )}
              {regenerating === 'all' ? `Regenerating ${batchProgress?.current}/${batchProgress?.total}...` : `Regenerate All (${totalFlagged})`}
            </button>
          )}
          <button
            onClick={runAudit}
            disabled={auditing}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-colors ${
              auditResult?.overall === 'pass'
                ? 'bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100'
                : auditResult?.overall === 'fail'
                  ? 'bg-red-50 border-red-300 text-red-700 hover:bg-red-100'
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100 hover:border-gray-300'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
            data-testid="bp-gallery-audit-btn"
          >
            {auditing ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <ShieldCheck size={13} />
            )}
            {auditing ? 'Auditing...' : auditResult ? (auditResult.overall === 'pass' ? 'Audit Passed' : 'Issues Found') : 'Verify Images'}
          </button>
          <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <CheckCircle size={14} className="text-emerald-600" />
            <span className="text-xs font-semibold text-emerald-700">{filteredTotal} images</span>
          </div>
        </div>
      </div>

      {/* Batch Progress Banner */}
      {batchProgress && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-5 py-3 flex items-center gap-3" data-testid="bp-batch-progress">
          <Loader2 size={18} className="animate-spin text-blue-600" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-blue-800">
              Regenerating image {batchProgress.current} of {batchProgress.total}
            </p>
            <p className="text-xs text-blue-600 mt-0.5">{batchProgress.currentContext}</p>
          </div>
          <div className="w-32 bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-500"
              style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Audit Results Panel */}
      {showAuditPanel && (
        <div className={`border-2 rounded-xl overflow-hidden transition-all ${
          auditResult?.overall === 'pass' ? 'border-emerald-300 bg-emerald-50/50' :
          auditResult?.overall === 'fail' ? 'border-red-300 bg-red-50/50' :
          'border-gray-200 bg-gray-50/50'
        }`} data-testid="bp-audit-panel">
          <div className="flex items-center justify-between px-5 py-3">
            <div className="flex items-center gap-3">
              {auditing ? (
                <Loader2 size={20} className="animate-spin text-blue-600" />
              ) : auditResult?.overall === 'pass' ? (
                <ShieldCheck size={20} className="text-emerald-600" />
              ) : (
                <AlertTriangle size={20} className="text-red-600" />
              )}
              <div>
                <h3 className="text-sm font-bold text-gray-900">
                  {auditing ? 'Running Image Audit...' :
                   auditResult?.overall === 'pass' ? 'All Checks Passed' : 'Issues Found'}
                </h3>
                {auditResult && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Audited at {new Date(auditResult.audited_at).toLocaleTimeString()}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={() => setShowAuditPanel(false)}
              className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
              data-testid="bp-audit-close"
            >
              <X size={16} />
            </button>
          </div>

          {auditResult && (
            <div className="px-5 pb-4 space-y-3">
              {/* Checks Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2">
                {auditResult.checks.map((check, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-2 px-3 py-2.5 rounded-lg border ${
                      check.status === 'pass' ? 'bg-emerald-50 border-emerald-200' :
                      check.status === 'warning' ? 'bg-amber-50 border-amber-200' :
                      'bg-red-50 border-red-200'
                    }`}
                    data-testid={`bp-audit-check-${i}`}
                  >
                    {check.status === 'pass' ? (
                      <CheckCircle size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                    ) : check.status === 'warning' ? (
                      <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                    ) : (
                      <AlertCircle size={14} className="text-red-600 mt-0.5 shrink-0" />
                    )}
                    <div>
                      <p className={`text-xs font-semibold ${
                        check.status === 'pass' ? 'text-emerald-800' :
                        check.status === 'warning' ? 'text-amber-800' : 'text-red-800'
                      }`}>{check.name}</p>
                      <p className={`text-[10px] mt-0.5 ${
                        check.status === 'pass' ? 'text-emerald-600' :
                        check.status === 'warning' ? 'text-amber-600' : 'text-red-600'
                      }`}>{check.detail}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Stats Summary */}
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="bg-white border border-gray-200 rounded-md px-2.5 py-1 text-gray-600">
                  <strong>{auditResult.stats.total_listening}</strong> listening Qs
                </span>
                <span className="bg-white border border-gray-200 rounded-md px-2.5 py-1 text-gray-600">
                  <strong>{auditResult.stats.unique_contexts}</strong> contexts
                </span>
                <span className="bg-white border border-gray-200 rounded-md px-2.5 py-1 text-gray-600">
                  <strong>{auditResult.stats.unique_images}</strong> unique images
                </span>
                <span className="bg-white border border-gray-200 rounded-md px-2.5 py-1 text-gray-600">
                  <strong>{auditResult.stats.files_found}</strong> files on disk
                </span>
              </div>

              {/* Issues List (if any) */}
              {auditResult.issues.length > 0 && (
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-gray-700">
                      {auditResult.critical_count} critical, {auditResult.warning_count} warning, {auditResult.info_count} info
                    </p>
                    {auditResult.info_count > 0 && auditResult.critical_count === 0 && (
                      <button
                        onClick={cleanOrphans}
                        disabled={cleaning}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 border border-amber-300 text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="bp-clean-orphans-btn"
                      >
                        {cleaning ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <Trash2 size={12} />
                        )}
                        {cleaning ? 'Cleaning...' : `Clean ${auditResult.info_count} Orphans`}
                      </button>
                    )}
                  </div>
                  {auditResult.issues.map((issue, i) => (
                    <div
                      key={i}
                      className={`flex items-start gap-2 px-3 py-1.5 rounded text-[11px] ${
                        issue.severity === 'critical' ? 'bg-red-50 text-red-700' :
                        issue.severity === 'warning' ? 'bg-amber-50 text-amber-700' :
                        'bg-gray-50 text-gray-600'
                      }`}
                      data-testid={`bp-audit-issue-${i}`}
                    >
                      {issue.severity === 'critical' ? <AlertCircle size={11} className="mt-0.5 shrink-0" /> :
                       issue.severity === 'warning' ? <AlertTriangle size={11} className="mt-0.5 shrink-0" /> :
                       <Info size={11} className="mt-0.5 shrink-0" />}
                      <span>
                        {issue.grade !== '-' && <strong>Grade {issue.grade}</strong>}
                        {issue.context !== '-' && <> &middot; {issue.context}</>}
                        {' '}&mdash; {issue.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Grade Sections */}
      {filteredGallery.map(gradeGroup => {
        const colors = GRADE_COLORS[gradeGroup.grade] || GRADE_COLORS['K'];
        const isExpanded = expandedGrades[gradeGroup.grade];
        const flaggedInGrade = gradeGroup.contexts.filter(c => c.flagged).length;

        return (
          <div
            key={gradeGroup.grade}
            className={`border-2 ${colors.border} rounded-xl overflow-hidden`}
            data-testid={`bp-gallery-grade-${gradeGroup.grade}`}
          >
            <button
              onClick={() => toggleGrade(gradeGroup.grade)}
              className={`w-full flex items-center justify-between px-5 py-3.5 ${colors.bg} hover:opacity-90 transition-opacity`}
              data-testid={`bp-gallery-toggle-${gradeGroup.grade}`}
            >
              <div className="flex items-center gap-3">
                <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${colors.badge}`}>
                  {gradeGroup.label}
                </span>
                <span className="text-sm text-gray-600">
                  {gradeGroup.contexts.length} scene{gradeGroup.contexts.length !== 1 ? 's' : ''}
                </span>
                {flaggedInGrade > 0 && (
                  <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                    <Flag size={11} className="fill-red-500 text-red-500" /> {flaggedInGrade}
                  </span>
                )}
              </div>
              {isExpanded ? <ChevronUp size={18} className="text-gray-400" /> : <ChevronDown size={18} className="text-gray-400" />}
            </button>

            {isExpanded && (
              <div className="p-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 bg-white">
                {gradeGroup.contexts.map((ctx, idx) => {
                  const isThisRegenerating = isRegenerating(gradeGroup.grade, ctx.context);
                  const thisSelected = isSelected(gradeGroup.grade, ctx.context);
                  return (
                    <div
                      key={idx}
                      className={`group relative border rounded-lg overflow-hidden hover:shadow-md transition-all cursor-pointer ${
                        thisSelected ? 'border-indigo-500 ring-2 ring-indigo-300' :
                        ctx.flagged ? 'border-red-300 ring-1 ring-red-200' : 'border-gray-100 hover:border-gray-300'
                      } ${isThisRegenerating ? 'opacity-60' : ''}`}
                      onClick={() => { if (!isThisRegenerating) { setLightboxImage({ ...ctx, grade: gradeGroup.label, gradeKey: gradeGroup.grade }); setFlagNote(ctx.flag_note || ''); }}}
                      data-testid={`bp-gallery-card-${gradeGroup.grade}-${idx}`}
                    >
                      {/* Select checkbox — stops click propagation so lightbox doesn't open */}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); if (!isThisRegenerating) toggleSelected(gradeGroup.grade, ctx.context); }}
                        className={`absolute top-2 left-2 z-10 w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                          thisSelected ? 'bg-indigo-600 border-indigo-700 text-white' : 'bg-white/90 border-gray-400 hover:border-indigo-500'
                        }`}
                        aria-label={thisSelected ? 'Deselect image' : 'Select image'}
                        data-testid={`bp-gallery-select-${gradeGroup.grade}-${idx}`}
                      >
                        {thisSelected && <CheckCircle size={14} />}
                      </button>
                      <div className="relative aspect-[3/2] bg-gray-50">
                        <img src={ctx.image_url} alt={ctx.context} className="w-full h-full object-cover" loading="lazy" />
                        {isThisRegenerating ? (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <Loader2 size={28} className="animate-spin text-white" />
                          </div>
                        ) : (
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                            <Maximize2 size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        )}
                        <div className="absolute top-2 right-2 bg-black/60 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                          {ctx.question_count}Q
                        </div>
                        {ctx.flagged && (
                          <div className="absolute bottom-2 left-2 bg-red-500 text-white p-1 rounded" data-testid={`bp-flag-icon-${gradeGroup.grade}-${idx}`}>
                            <Flag size={12} className="fill-white" />
                          </div>
                        )}
                      </div>
                      <div className="px-3 py-2">
                        <p className="text-xs font-medium text-gray-800 leading-tight line-clamp-2">{ctx.context}</p>
                        {ctx.flagged && ctx.flag_note && (
                          <p className="text-[10px] text-red-500 mt-1 line-clamp-1 flex items-center gap-1">
                            <MessageSquare size={9} /> {ctx.flag_note}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {filteredGallery.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <ImageIcon size={48} className="mx-auto mb-3 opacity-40" />
          <p className="text-sm">{showFlaggedOnly ? 'No flagged images.' : 'No images match your search.'}</p>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="bp-gallery-lightbox"
        >
          <div
            className="relative max-w-5xl w-full bg-white rounded-xl overflow-hidden shadow-2xl max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setLightboxImage(null)}
              className="absolute top-3 right-3 z-10 bg-black/50 hover:bg-black/70 text-white rounded-full p-1.5 transition-colors"
              data-testid="bp-gallery-lightbox-close"
            >
              <X size={18} />
            </button>
            <img
              src={lightboxImage.image_url}
              alt={lightboxImage.context}
              className="w-full max-h-[55vh] object-contain bg-gray-900"
            />
            <div className="px-6 py-4 border-t border-gray-100">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-base font-bold text-gray-900">{lightboxImage.context}</p>
                  <p className="text-sm text-gray-500 mt-0.5">{lightboxImage.grade}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span className="bg-blue-50 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-md border border-blue-200">
                    {lightboxImage.question_count} question{lightboxImage.question_count !== 1 ? 's' : ''}
                  </span>
                  <span className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-1 rounded-md border border-emerald-200">
                    Text-free
                  </span>
                </div>
              </div>
              {lightboxImage.sample_question && (
                <p className="text-xs text-gray-400 mt-2 italic">
                  Sample: &ldquo;{lightboxImage.sample_question}&rdquo;
                </p>
              )}

              {/* Flag Controls */}
              <div className={`mt-4 pt-4 border-t ${lightboxImage.flagged ? 'border-red-100' : 'border-gray-100'}`}>
                {lightboxImage.flagged ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Flag size={14} className="text-red-500 fill-red-500" />
                        <span className="text-sm font-semibold text-red-700">Flagged for Review</span>
                        {lightboxImage.flag_by && (
                          <span className="text-xs text-gray-400">by {lightboxImage.flag_by}</span>
                        )}
                      </div>
                      <button
                        onClick={() => handleUnflag(lightboxImage.gradeKey, lightboxImage.context)}
                        disabled={flagging}
                        className="text-xs text-gray-500 hover:text-red-600 font-medium px-2 py-1 rounded hover:bg-red-50 transition-colors"
                        data-testid="bp-lightbox-unflag-btn"
                      >
                        {flagging ? 'Removing...' : 'Remove Flag'}
                      </button>
                    </div>
                    {lightboxImage.flag_note && (
                      <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                        <p className="text-xs text-red-700">{lightboxImage.flag_note}</p>
                      </div>
                    )}
                    {/* Regenerate Button */}
                    <button
                      onClick={() => handleRegenerate(lightboxImage.gradeKey, lightboxImage.context)}
                      disabled={!!regenerating}
                      className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="bp-lightbox-regenerate-btn"
                    >
                      {isRegenerating(lightboxImage.gradeKey, lightboxImage.context) ? (
                        <>
                          <Loader2 size={15} className="animate-spin" />
                          Generating new image...
                        </>
                      ) : (
                        <>
                          <RefreshCw size={15} />
                          Regenerate Image
                        </>
                      )}
                    </button>
                    {isRegenerating(lightboxImage.gradeKey, lightboxImage.context) && (
                      <p className="text-[11px] text-center text-blue-500">
                        the automated system is crafting a new WIDA-aligned illustration. This takes 30-60 seconds...
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Flag size={14} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-600">Flag for Review</span>
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Optional note (e.g., 'text visible on banner')..."
                        value={flagNote}
                        onChange={(e) => setFlagNote(e.target.value)}
                        className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300"
                        data-testid="bp-lightbox-flag-note"
                        onKeyDown={(e) => { if (e.key === 'Enter') handleFlag(lightboxImage.gradeKey, lightboxImage.grade, lightboxImage.context, flagNote); }}
                      />
                      <button
                        onClick={() => handleFlag(lightboxImage.gradeKey, lightboxImage.grade, lightboxImage.context, flagNote)}
                        disabled={flagging}
                        className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                        data-testid="bp-lightbox-flag-btn"
                      >
                        <Flag size={13} className="fill-white" />
                        {flagging ? 'Flagging...' : 'Flag'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BigPictureGalleryTab;
